var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef } from 'react';
import { useDialog } from '@react-aria/dialog';
import { OverlayContainer, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { useFocusTrapping } from '../../utils/hooks/useFocusTrapping';
import { Box } from '../Box/Box';
import { IconButton } from '../IconButton/IconButton';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { ThemeProvider } from '../ThemeProvider/ThemeProvider';
import { Typography } from '../Typography/Typography';
import { PREVIOUS_ACTION_EMPTY_SPACING, modal, modalTitle, modalTitleContainer } from './styles';
export const Modal = (_a) => {
    var { isOpen, theme } = _a, props = __rest(_a, ["isOpen", "theme"]);
    usePreventScroll({ isDisabled: !isOpen });
    if (!isOpen) {
        return null;
    }
    return (_jsx(OverlayContainer, { children: theme ? (_jsx(ThemeProvider, { local: true, theme: theme, children: _jsx(OpenModal, Object.assign({}, props)) })) : (_jsx(OpenModal, Object.assign({}, props))) }));
};
const OpenModal = (props) => {
    var _a;
    const { children, title, srTitle, actionLabels, onClose, onPreviousAction, className } = props, boxProps = __rest(props
    // Handles interacting outside the dialog and pressing
    // the Escape key to close the modal.
    , ["children", "title", "srTitle", "actionLabels", "onClose", "onPreviousAction", "className"]);
    // Handles interacting outside the dialog and pressing
    // the Escape key to close the modal.
    const ref = useRef(null);
    useFocusTrapping(ref);
    const { overlayProps, underlayProps } = useOverlay({
        isOpen: true,
        onClose,
        isDismissable: true,
    }, ref);
    const { dialogProps, titleProps } = useDialog({}, ref);
    const srTitleId = useId();
    useEffect(() => {
        if (!(title || srTitle)) {
            console.warn('Modal: A title or srTitle prop is required for accessibility', ref.current);
        }
    }, [title, srTitle]);
    return (_jsx("div", Object.assign({ className: "fixed inset-0 z-50 flex items-center justify-center bg-black-900/60" }, underlayProps, { children: _jsxs(Box, Object.assign({}, overlayProps, dialogProps, { ref: ref, className: modal({ className }) }, boxProps, { maxHeight: props.maxHeight || '100vh', maxWidth: props.maxWidth || '480px', width: props.width || '100%' }, (Boolean(srTitle) && { 'aria-labelledby': `${(_a = dialogProps['aria-labelledby']) !== null && _a !== void 0 ? _a : ''} ${srTitleId}` }), { "aria-modal": true, children: [(onPreviousAction || title || onClose) && (_jsxs("div", { className: modalTitleContainer({
                        justifyContent: Boolean(onPreviousAction || title),
                        marginBottom: Boolean(title),
                    }), children: [onPreviousAction ? (_jsx(IconButton, { "aria-label": actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.previousAction, size: 32, variant: "text", name: "chevron-left", onClick: onPreviousAction })) : (_jsx("div", { className: `w-[${PREVIOUS_ACTION_EMPTY_SPACING}]` })), title ? (_jsx("div", { className: modalTitle({
                                alignItems: Boolean(onPreviousAction),
                            }), children: typeof title === 'string' ? (_jsx(Typography, Object.assign({ variant: "h2" }, titleProps, { align: "center", children: title }))) : (title) })) : null, srTitle ? (_jsx(ScreenReaderOnly, { id: srTitleId, children: _jsx("h2", { children: srTitle }) })) : null, onClose ? (_jsx(IconButton, { "aria-label": actionLabels === null || actionLabels === void 0 ? void 0 : actionLabels.close, size: 32, variant: "text", name: "cancel", onClick: onClose })) : null] })), children] })) })));
};
